import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Footer } from '@components/molecules/Footer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import sidebarStyles from './UserAccountSidebar.module.css';
import styles from './UserAccountContainer.module.css';
import { Header } from '@components/molecules/Header';

type UserAccountContainerSubComponents = {
    Filters: typeof Filters;
};

interface UserAccountContainerProps {
    title: string;
    overrideOnDesktop?: JSX.Element;
    children: JSX.Element | JSX.Element[];
}

export const UserAccountContainer: FC<UserAccountContainerProps> & UserAccountContainerSubComponents = ({
    title,
    overrideOnDesktop,
    children,
}: UserAccountContainerProps) => {
    const { navigate } = useCustomNavigate();
    const { login } = useData();
    const { t } = useTranslation();

    const translations = {
        sidebarTitle: t('user-account-sidebar.title'),
    };

    // if user is not logged in, redirect to login
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    return (
        <>
            <Header />

            <div className="position-relative" data-testid="user-account-container">
                <Container classes="px-3 mb-5 pt-5 py-lg-5">
                    <div className="d-flex gap-4">
                        <UserAccountSidebar />
                        <div className="user-account-content w-100 flex-grow-1">
                            {/* open sidebar */}
                            <Typography classes="d-lg-none mb-3">
                                <a
                                    className="text-decoration-none text-dark"
                                    data-bs-toggle="collapse"
                                    href="#collapseUserSidebar"
                                    role="button"
                                    aria-expanded="false"
                                    aria-controls="collapseUserSidebar">
                                    <i className="fa-light fa-arrow-left me-2" /> {translations.sidebarTitle}
                                </a>
                            </Typography>
                            {!overrideOnDesktop ? (
                                <Typography
                                    appearanceLevel={TypographyAppearanceVariants.level6}
                                    tagVariant={TypographyTagVariants.h1}>
                                    {title}
                                </Typography>
                            ) : (
                                overrideOnDesktop
                            )}

                            {children}
                        </div>
                    </div>
                </Container>
            </div>

            <Footer mode="normal" />
        </>
    );
};

interface FiltersProps {
    children: JSX.Element;
}

const Filters: FC<FiltersProps> = ({ children }) => {
    return (
        <div className={styles.filters} data-testid="user-account-filters">
            {children}
        </div>
    );
};

UserAccountContainer.Filters = Filters;

const UserAccountSidebar = () => {
    const { pathname } = useLocation();
    const { login, user } = useData();
    const { t } = useTranslation();

    const translations = {
        greeting: t('user-account-sidebar.greeting', { userName: login?.data?.user?.name || '' }),
        title: t('user-account-sidebar.title'),
        myProfile: t('user-account-sidebar.my-profile'),
        mySpaces: t('user-account-sidebar.my-spaces'),
        createSpace: t('user-account-sidebar.create-space'),
        alerts: t('user-account-sidebar.alerts'),
    };

    // Permission checks based on user type
    const userType = user?.data?.user_type;
    const canViewSpaces = userType === 'Imobiliária' || userType === 'Proprietário';
    const canCreateSpaces = userType === 'Imobiliária' || userType === 'Proprietário';
    const canViewAlerts = userType === 'Utilizador Público';

    const sidebarLinks = [
        { title: translations.myProfile, link: SITE_URLS.myProfile, icon: 'fa-light fa-user' },
        canViewSpaces && { title: translations.mySpaces, link: SITE_URLS.mySpaces, icon: 'fa-light fa-building' },
        canCreateSpaces && { title: translations.createSpace, link: SITE_URLS.createSpace, icon: 'fa-light fa-plus' },
        canViewAlerts && { title: translations.alerts, link: SITE_URLS.alerts, icon: 'fa-light fa-bell' },
    ].filter(Boolean) as { title: string; icon: string; link: string }[];

    return (
        <div
            id="collapseUserSidebar"
            className={`${sidebarStyles['user-account-sidebar']} collapse collapse-horizontal sticky-top`}>
            <div
                className={`${sidebarStyles['sidebar-content']} d-lg-flex flex-column h-100 my-lg-0 me-lg-1 py-5 py-lg-0 pe-lg-4`}>
                <div className="container p-lg-0">
                    <Typography classes="d-lg-none mb-3" appearanceLevel={TypographyAppearanceVariants.level2}>
                        {translations.greeting}
                    </Typography>
                    <Typography classes="text-decoration-none" appearanceLevel={TypographyAppearanceVariants.level7}>
                        {translations.title}
                    </Typography>
                    <hr className="mb-2" />
                    <ul className="nav flex-column mb-auto">
                        {sidebarLinks.map((item, index) => {
                            // remove initial '/' from pathname
                            const isActive = pathname.substring(1) === item.link;

                            return (
                                <li key={`menu-item-${index}`}>
                                    <Anchor
                                        href={item.link}
                                        classes={`nav-link ${sidebarStyles['nav-link']} ${isActive && item.link !== '' ? sidebarStyles.active : ''}`}
                                        mode={AnchorMode.none}>
                                        <>
                                            <i className={item.icon} /> {item.title}
                                        </>
                                    </Anchor>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="d-none d-lg-block sidebar-vertical-separator"></div>
            </div>
        </div>
    );
};
